import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Collapse,
  Grid,
  Alert,
  CircularProgress,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
  Computer as ComputerIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import { useGetMyActivityLogsQuery } from '../../features/logs/logsApiSlice';

const UserActivityLogs = () => {
  // State management
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null
  });
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Query parameters
  const queryParams = useMemo(() => ({
    page: page + 1,
    limit: rowsPerPage,
    ...(searchTerm && { search: searchTerm }),
    ...(dateRange.startDate && { startDate: dateRange.startDate.toISOString() }),
    ...(dateRange.endDate && { endDate: dateRange.endDate.toISOString() })
  }), [page, rowsPerPage, searchTerm, dateRange]);

  // API call
  const {
    data: logsData,
    error: logsError,
    isLoading: logsLoading,
    refetch: refetchLogs
  } = useGetMyActivityLogsQuery(queryParams);

  // Event handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0);
  };

  const handleClearFilters = () => {
    setDateRange({
      startDate: null,
      endDate: null
    });
    setSearchTerm('');
    setPage(0);
  };

  const handleRowExpand = (logId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedRows(newExpanded);
  };

  // Utility functions
  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'error': return 'error';
      case 'warn': return 'warning';
      case 'info': return 'info';
      default: return 'default';
    }
  };

  const getActionIcon = (action) => {
    if (action.includes('LOGIN')) return <SecurityIcon fontSize="small" />;
    if (['CREATE', 'UPDATE', 'DELETE'].includes(action)) return <ComputerIcon fontSize="small" />;
    return <PersonIcon fontSize="small" />;
  };

  const formatTimestamp = (timestamp) => {
    try {
      return format(parseISO(timestamp), 'MMM dd, yyyy HH:mm:ss');
    } catch (error) {
      return timestamp;
    }
  };

  // Helper function to safely render any value
  const safeRender = (value, fallback = 'N/A') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  const exportLogs = () => {
    if (!logsData?.logs) return;
    
    const csvContent = [
      ['Timestamp', 'Action', 'Collection', 'Status', 'Browser', 'OS', 'Device', 'IP Address', 'Message'].join(','),
      ...logsData.logs.map(log => [
        formatTimestamp(log.timestamp),
        safeRender(log.action),
        safeRender(log.collectionName),
        safeRender(log.status),
        safeRender(log.browser),
        safeRender(log.os),
        safeRender(log.device),
        safeRender(log.ipAddress),
        `"${safeRender(log.message)}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `my-activity-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (logsError) {
    console.error('User Activity Logs Error:', logsError);
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading your activity logs: {logsError?.message || logsError?.data?.message || logsError?.error || 'Unknown error'}
        </Alert>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Status: {logsError?.status || 'Unknown'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please check your network connection and try again.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            My Activity Logs
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View your personal activity history and actions
          </Typography>
        </Box>

        {/* Search and Filter Controls */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <TextField
              placeholder="Search your activities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              size="small"
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetchLogs()}
              disabled={logsLoading}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportLogs}
              disabled={!logsData?.logs?.length}
            >
              Export
            </Button>
          </Stack>

          {/* Date Range Filters */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="Start Date"
                value={dateRange.startDate}
                onChange={(date) => handleDateChange('startDate', date)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="End Date"
                value={dateRange.endDate}
                onChange={(date) => handleDateChange('endDate', date)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                fullWidth
                sx={{ height: '40px' }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Logs Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="50px"></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ScheduleIcon sx={{ mr: 1, fontSize: 16 }} />
                      Timestamp
                    </Box>
                  </TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Collection</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Browser</TableCell>
                  <TableCell>OS</TableCell>
                  <TableCell>Device</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logsLoading ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : logsData?.logs?.length > 0 ? (
                  logsData.logs.map((log) => (
                    <React.Fragment key={log._id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleRowExpand(log._id)}
                          >
                            {expandedRows.has(log._id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatTimestamp(log.timestamp)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getActionIcon(log.action)}
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              {log.action}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {safeRender(log.collectionName)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.status}
                            color={getStatusColor(log.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.level}
                            color={getLevelColor(log.level)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {safeRender(log.browser)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {safeRender(log.os)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {safeRender(log.device)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {safeRender(log.ipAddress)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {safeRender(log.message)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={11} sx={{ py: 0 }}>
                          <Collapse in={expandedRows.has(log._id)}>
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Details
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Document ID:</strong> {safeRender(log.documentId)}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Browser:</strong> {safeRender(log.browser)}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Operating System:</strong> {safeRender(log.os)}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Device Type:</strong> {safeRender(log.device)}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Full Message:</strong> {safeRender(log.message)}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Changes
                                  </Typography>
                                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                                    <pre style={{ 
                                      fontSize: '12px', 
                                      margin: 0, 
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word'
                                    }}>
                                      {log.changes ? JSON.stringify(log.changes, null, 2) : 'No changes recorded'}
                                    </pre>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No activity logs found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {logsData && (
            <TablePagination
              component="div"
              count={logsData.total || 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              showFirstButton
              showLastButton
            />
          )}
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default UserActivityLogs;
